import Papa from "papaparse";
import { getListings, getListing } from "../api/realestate";
import { getWarps, getWarp } from "../api/warps";
import { getBlogPosts, getBlogPost } from "../api/blog";
import {
  getForumBoards,
  getForumCategories,
  getForumBoardCategories,
  getForumCategoryThreads,
  getForumThread,
  getForumProfile,
  normalizeForumProfileData,
} from "../api/forum";
import { getNotifications } from "../api/notifications";
import { getTickets, getTicket } from "../api/tickets";
import { getAppeals, getAppeal } from "../api/appeals";
import { getPlayerProfile } from "../api/profiles";
import { listDmThreads, getDmThread } from "../api/messages";
import { getApprovedSubmissions } from "../api/submissions";
import { fetchAllMySubmissions } from "../utils/mySubmissions";
import { getMyUploads } from "../api/uploads";
import { getDashboard } from "../api/admin";
import { listTournaments, getTournament } from "../api/tournaments";
import { DIRECTORY_PAGE_LIMIT, isReactSnapCrawler } from "../utils/prerenderData";
import { fetchRouteCache, setRouteCache } from "../utils/routeDataCache";
import {
  realEstateListKey,
  warpsListKey,
  blogListKey,
  blogPostKey,
  warpDetailKey,
  realEstateDetailKey,
  MARKETS_ALL_TIME_KEY,
  MARKETS_MONTHLY_KEY,
  forumHomeKey,
  forumBoardKey,
  forumCategoryKey,
  forumThreadKey,
  forumNotificationsKey,
  forumProfileKey,
  playerProfileKey,
  supportTicketsKey,
  supportAppealsKey,
  supportTicketDetailKey,
  supportAppealDetailKey,
  messagesListKey,
  messagesThreadKey,
  submissionsKey,
  userSubmissionsKey,
  myUploadKey,
  adminDashboardKey,
  homeWarpsPreviewKey,
  homeSubmissionsPreviewKey,
  tournamentsListKey,
  tournamentDetailKey,
} from "../utils/routeCacheKeys";

const TSV_PARSE_OPTIONS = {
  header: true,
  delimiter: "\t",
  skipEmptyLines: true,
  dynamicTyping: true,
};

async function fetchAndParseTsv(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch data: ${response.status}`);
  }
  const rawText = await response.text();
  return new Promise((resolve, reject) => {
    Papa.parse(rawText, {
      ...TSV_PARSE_OPTIONS,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error(`Parse error: ${results.errors[0].message}`));
          return;
        }
        resolve(results.data);
      },
      error: (err) => reject(err),
    });
  });
}

async function prefetchMarketsTsv() {
  await Promise.all([
    fetchRouteCache(MARKETS_ALL_TIME_KEY, () =>
      fetchAndParseTsv("/markets-data/generated/latest-all-time-avg.tsv")
    ),
    fetchRouteCache(MARKETS_MONTHLY_KEY, () =>
      fetchAndParseTsv("/markets-data/generated/latest-all-time-by-month.tsv")
    ),
  ]);
}

function parseSearchParams(search) {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

async function prefetchRealEstateList(search) {
  const params = parseSearchParams(search);
  const key = realEstateListKey({
    search: params.get("search") || "",
    type: params.get("type") || "",
    status: params.get("status") || "",
  });
  await fetchRouteCache(key, async () => {
    const data = await getListings({
      search: params.get("search") || undefined,
      type: params.get("type") || undefined,
      status: params.get("status") || undefined,
      limit: DIRECTORY_PAGE_LIMIT,
    });
    return data.listings;
  });
}

async function prefetchWarpsList(search) {
  const params = parseSearchParams(search);
  const key = warpsListKey({
    search: params.get("search") || "",
    type: params.get("type") || "",
  });
  await fetchRouteCache(key, async () => {
    const data = await getWarps({
      search: params.get("search") || undefined,
      type: params.get("type") || undefined,
      limit: DIRECTORY_PAGE_LIMIT,
    });
    return data.warps;
  });
}

async function prefetchBlogList(search) {
  const params = parseSearchParams(search);
  const topic = params.get("topic") || "";
  const tag = params.get("tag") || "";
  const key = blogListKey({ topic, tag });
  await fetchRouteCache(key, async () => {
    const data = await getBlogPosts({
      topic: topic || undefined,
      tag: tag || undefined,
    });
    return data.posts || [];
  });
}

async function prefetchBlogPost(slug) {
  const key = blogPostKey(slug);
  await fetchRouteCache(key, async () => {
    const data = await getBlogPost(slug);
    return data.post;
  });
}

async function prefetchWarpDetail(id) {
  const key = warpDetailKey(id);
  await fetchRouteCache(key, async () => {
    const data = await getWarp(id);
    return data.warp;
  });
}

async function prefetchRealEstateDetail(id) {
  const key = realEstateDetailKey(id);
  await fetchRouteCache(key, async () => {
    const data = await getListing(id);
    return data.listing;
  });
}

async function prefetchForumHome() {
  await fetchRouteCache(forumHomeKey(), async () => {
    const [boardData, categoryData] = await Promise.all([
      getForumBoards(),
      getForumCategories(),
    ]);
    return {
      boards: boardData.boards || [],
      categories: categoryData.categories || [],
    };
  });
}

async function prefetchForumBoard(boardId) {
  await fetchRouteCache(forumBoardKey(boardId), async () => {
    const data = await getForumBoardCategories(boardId);
    return {
      board: data.board || null,
      categories: data.categories || [],
    };
  });
}

async function prefetchForumCategory(categoryId) {
  await fetchRouteCache(forumCategoryKey(categoryId), async () => {
    const data = await getForumCategoryThreads(categoryId);
    return data;
  });
}

async function prefetchForumThread(threadId) {
  await fetchRouteCache(forumThreadKey(threadId), async () => {
    const data = await getForumThread(threadId);
    return data;
  });
}

async function prefetchForumNotifications(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    setRouteCache(forumNotificationsKey(), []);
    return;
  }
  await fetchRouteCache(forumNotificationsKey(), async () => {
    const data = await getNotifications({ limit: 50 });
    return data.notifications || [];
  });
}

async function prefetchForumProfile(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    setRouteCache(forumProfileKey(), {});
    return;
  }
  await fetchRouteCache(forumProfileKey(), async () => {
    const data = await getForumProfile();
    return normalizeForumProfileData(data);
  });
}

async function prefetchPlayerProfile(webUsername, authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  const key = playerProfileKey(webUsername);
  await fetchRouteCache(key, async () => getPlayerProfile(webUsername));
}

async function prefetchSupportTickets(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    setRouteCache(supportTicketsKey(), []);
    return;
  }
  await fetchRouteCache(supportTicketsKey(), async () => {
    const data = await getTickets();
    return data.tickets;
  });
}

async function prefetchSupportAppeals(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    setRouteCache(supportAppealsKey(), []);
    return;
  }
  await fetchRouteCache(supportAppealsKey(), async () => {
    const data = await getAppeals();
    return data.appeals;
  });
}

async function prefetchSupportTicketDetail(id, authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  await fetchRouteCache(supportTicketDetailKey(id), async () => {
    const data = await getTicket(id);
    return data.ticket;
  });
}

async function prefetchSupportAppealDetail(id, authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  await fetchRouteCache(supportAppealDetailKey(id), async () => {
    const data = await getAppeal(id);
    return data.appeal;
  });
}

async function prefetchMessages(authContext, threadId) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    return;
  }
  if (threadId) {
    await fetchRouteCache(messagesThreadKey(threadId), async () => {
      const data = await getDmThread(threadId);
      return data;
    });
  } else {
    await fetchRouteCache(messagesListKey(), async () => {
      const data = await listDmThreads();
      return data.threads || [];
    });
  }
}

async function prefetchSubmissions(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    setRouteCache(submissionsKey(), []);
    return;
  }
  await fetchRouteCache(submissionsKey(), fetchAllMySubmissions);
}

async function prefetchUserSubmissions() {
  await fetchRouteCache(userSubmissionsKey(1), async () => {
    const data = await getApprovedSubmissions({ page: 1, limit: 48 });
    return data.submissions || [];
  });
}

async function prefetchMyUpload(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  if (!authContext?.isAuthenticated) {
    return;
  }
  await fetchRouteCache(myUploadKey(), async () => {
    const data = await getMyUploads();
    return data.uploads || [];
  });
}

const HOME_PREVIEW_LIMIT = 6;

async function prefetchHomePreviews() {
  await Promise.all([
    fetchRouteCache(homeWarpsPreviewKey(HOME_PREVIEW_LIMIT), async () => {
      const data = await getWarps({ limit: HOME_PREVIEW_LIMIT });
      return data.warps || [];
    }),
    fetchRouteCache(homeSubmissionsPreviewKey(HOME_PREVIEW_LIMIT), async () => {
      const data = await getApprovedSubmissions({ limit: HOME_PREVIEW_LIMIT });
      return data.submissions || [];
    }),
  ]);
}

async function prefetchTournamentsList(search) {
  const params = parseSearchParams(search);
  const status = params.get("status") || "";
  const key = tournamentsListKey({ status });
  await fetchRouteCache(key, async () => {
    const data = await listTournaments({ status: status || undefined });
    return data.tournaments || [];
  });
}

async function prefetchTournamentDetail(slug) {
  await fetchRouteCache(tournamentDetailKey(slug), async () => {
    const data = await getTournament(slug);
    return data.tournament;
  });
}

async function prefetchAdminDashboard(authContext) {
  if (authContext?.authLoading) {
    await authContext.waitForAuth();
  }
  await fetchRouteCache(adminDashboardKey(), async () => getDashboard());
}

/**
 * Returns true if this route needs async prefetch before display.
 */
export function routeNeedsPrefetch(pathname) {
  if (pathname === "/") return true;
  if (pathname === "/real-estate") return true;
  if (pathname === "/warps") return true;
  if (pathname === "/blog") return true;
  if (pathname === "/markets") return true;
  if (pathname === "/forum") return true;
  if (/^\/warps\/[^/]+$/.test(pathname) && !pathname.endsWith("/submit") && !pathname.includes("/edit")) return true;
  if (/^\/real-estate\/[^/]+$/.test(pathname) && pathname !== "/real-estate/new" && !pathname.includes("/edit")) return true;
  if (/^\/blog\/[^/]+$/.test(pathname)) return true;
  if (/^\/forum\/board\/[^/]+$/.test(pathname)) return true;
  if (/^\/forum\/board\/[^/]+\/category\/[^/]+$/.test(pathname) && !pathname.endsWith("/new")) return true;
  if (/^\/forum\/category\/[^/]+$/.test(pathname) && !pathname.endsWith("/new")) return true;
  if (/^\/forum\/thread\/[^/]+$/.test(pathname)) return true;
  if (pathname === "/forum/notifications" || pathname === "/notifications") return true;
  if (pathname === "/forum/profile") return true;
  if (/^\/u\/[^/]+$/.test(pathname)) return true;
  if (pathname === "/support/tickets") return true;
  if (pathname === "/support/appeals") return true;
  if (/^\/support\/[^/]+$/.test(pathname) && pathname !== "/support/tickets" && pathname !== "/support/appeals" && !pathname.endsWith("/new")) return true;
  if (/^\/support\/appeals\/[^/]+$/.test(pathname)) return true;
  if (pathname === "/messages" || /^\/messages\/[^/]+$/.test(pathname)) return true;
  if (
    pathname === "/submissions/blog/my-submissions" ||
    pathname === "/submissions/warps/my-submissions" ||
    pathname === "/submissions/real-estate/my-submissions" ||
    pathname === "/submissions/screenshot/my-submissions"
  ) {
    return true;
  }
  if (pathname === "/user-submissions") return true;
  if (pathname === "/pixel-paint-2026") return true;
  if (pathname === "/submissions/pixel-paint-2026") return true;
  if (pathname === "/my-upload") return true;
  if (pathname === "/admin") return true;
  if (/^\/admin\/tickets\/[^/]+$/.test(pathname)) return true;
  if (/^\/admin\/appeals\/[^/]+$/.test(pathname)) return true;
  if (pathname === "/tournaments") return true;
  if (/^\/tournaments\/[^/]+$/.test(pathname) && pathname !== "/tournaments/new" && !pathname.endsWith("/edit") && !pathname.includes("/matches/")) return true;
  return false;
}

/**
 * Prefetch route data into routeDataCache. Resolves when ready to display.
 */
export async function prefetchRoute(pathname, search, authContext) {
  if (isReactSnapCrawler()) {
    return;
  }

  try {
    if (pathname === "/") {
      await prefetchHomePreviews();
      return;
    }
    if (pathname === "/real-estate") {
      await prefetchRealEstateList(search);
      return;
    }
    if (pathname === "/warps") {
      await prefetchWarpsList(search);
      return;
    }
    if (pathname === "/blog") {
      await prefetchBlogList(search);
      return;
    }
    if (pathname === "/markets") {
      await prefetchMarketsTsv();
      return;
    }
    if (pathname === "/forum") {
      await prefetchForumHome();
      return;
    }

    const warpDetailMatch = pathname.match(/^\/warps\/([^/]+)$/);
    if (warpDetailMatch && warpDetailMatch[1] !== "submit") {
      await prefetchWarpDetail(warpDetailMatch[1]);
      return;
    }

    const realEstateDetailMatch = pathname.match(/^\/real-estate\/([^/]+)$/);
    if (
      realEstateDetailMatch &&
      realEstateDetailMatch[1] !== "new" &&
      !pathname.includes("/edit")
    ) {
      await prefetchRealEstateDetail(realEstateDetailMatch[1]);
      return;
    }

    const blogPostMatch = pathname.match(/^\/blog\/([^/]+)$/);
    if (blogPostMatch) {
      await prefetchBlogPost(blogPostMatch[1]);
      return;
    }

    const forumBoardMatch = pathname.match(/^\/forum\/board\/([^/]+)$/);
    if (forumBoardMatch) {
      await prefetchForumBoard(forumBoardMatch[1]);
      return;
    }

    const forumBoardCategoryMatch = pathname.match(
      /^\/forum\/board\/([^/]+)\/category\/([^/]+)$/
    );
    if (forumBoardCategoryMatch && !pathname.endsWith("/new")) {
      await prefetchForumCategory(forumBoardCategoryMatch[2]);
      return;
    }

    const forumCategoryMatch = pathname.match(/^\/forum\/category\/([^/]+)$/);
    if (forumCategoryMatch && !pathname.endsWith("/new")) {
      await prefetchForumCategory(forumCategoryMatch[1]);
      return;
    }

    const forumThreadMatch = pathname.match(/^\/forum\/thread\/([^/]+)$/);
    if (forumThreadMatch) {
      await prefetchForumThread(forumThreadMatch[1]);
      return;
    }

    if (pathname === "/forum/notifications" || pathname === "/notifications") {
      await prefetchForumNotifications(authContext);
      return;
    }

    if (pathname === "/forum/profile") {
      await prefetchForumProfile(authContext);
      return;
    }

    const profileMatch = pathname.match(/^\/u\/([^/]+)$/);
    if (profileMatch) {
      await prefetchPlayerProfile(profileMatch[1], authContext);
      return;
    }

    if (pathname === "/support/tickets") {
      await prefetchSupportTickets(authContext);
      return;
    }

    if (pathname === "/support/appeals") {
      await prefetchSupportAppeals(authContext);
      return;
    }

    const appealDetailMatch = pathname.match(/^\/support\/appeals\/([^/]+)$/);
    if (appealDetailMatch) {
      await prefetchSupportAppealDetail(appealDetailMatch[1], authContext);
      return;
    }

    const ticketDetailMatch = pathname.match(/^\/support\/([^/]+)$/);
    if (
      ticketDetailMatch &&
      ticketDetailMatch[1] !== "tickets" &&
      ticketDetailMatch[1] !== "appeals"
    ) {
      await prefetchSupportTicketDetail(ticketDetailMatch[1], authContext);
      return;
    }

    if (pathname === "/messages") {
      await prefetchMessages(authContext);
      return;
    }

    const messagesThreadMatch = pathname.match(/^\/messages\/([^/]+)$/);
    if (messagesThreadMatch) {
      await prefetchMessages(authContext, messagesThreadMatch[1]);
      return;
    }

    if (
      pathname === "/submissions/blog/my-submissions" ||
      pathname === "/submissions/warps/my-submissions" ||
      pathname === "/submissions/real-estate/my-submissions" ||
      pathname === "/submissions/screenshot/my-submissions"
    ) {
      await prefetchSubmissions(authContext);
      return;
    }

    if (pathname === "/user-submissions") {
      await prefetchUserSubmissions();
      return;
    }

    if (pathname === "/my-upload") {
      await prefetchMyUpload(authContext);
      return;
    }

    if (pathname === "/admin") {
      await prefetchAdminDashboard(authContext);
      return;
    }

    const adminTicketMatch = pathname.match(/^\/admin\/tickets\/([^/]+)$/);
    if (adminTicketMatch) {
      await prefetchSupportTicketDetail(adminTicketMatch[1], authContext);
      return;
    }

    const adminAppealMatch = pathname.match(/^\/admin\/appeals\/([^/]+)$/);
    if (adminAppealMatch) {
      await prefetchSupportAppealDetail(adminAppealMatch[1], authContext);
      return;
    }

    if (pathname === "/tournaments") {
      await prefetchTournamentsList(search);
      return;
    }

    const tournamentDetailMatch = pathname.match(/^\/tournaments\/([^/]+)$/);
    if (
      tournamentDetailMatch &&
      tournamentDetailMatch[1] !== "new" &&
      !pathname.endsWith("/edit") &&
      !pathname.includes("/matches/")
    ) {
      await prefetchTournamentDetail(tournamentDetailMatch[1]);
      return;
    }
  } catch (err) {
    // Prefetch failure should not block navigation; page handles errors.
    console.warn("Route prefetch failed:", pathname, err);
  }
}

export { prefetchMarketsTsv };
